<script setup lang="ts">
import { computed, ref, watch, withDefaults } from 'vue';
import draggable from 'vuedraggable';

import DragDotsSvg from '@/assets/svg/jobs/drag-dots.svg?component';
import TrashIcon from '@/assets/svg/conversations/TrashBigIcon.svg?component';
import PlusIcon from '@/assets/svg/plus-16.svg?component';
import ChevronDownIcon from '@/assets/svg/chevron-down.svg?component';
import type { DynamicScreeningQuestion } from '@factoryfixinc/ats-interfaces';

const MAX_QUESTIONS = 99;

const props = withDefaults(
  defineProps<{
    isDisabled: boolean;
    questions?: DynamicScreeningQuestion[];
  }>(),
  {
    questions: () => [],
  },
);

const isCollapsed = ref(true);
const shouldDisplayErrors = ref(false);

const emit = defineEmits<{
  (event: 'update:questions', value: DynamicScreeningQuestion[]): void;
}>();

const editableQuestions = computed<DynamicScreeningQuestion[]>({
  get: () => props.questions ?? [],
  set: (value) => {
    emit('update:questions', value.slice(0, MAX_QUESTIONS));
  },
});

const addButtonDisabled = computed(
  () => props.isDisabled || editableQuestions.value.length >= MAX_QUESTIONS,
);

// Automatically expand the list if there are questions
watch(
  () => props.questions?.length ?? 0,
  (length) => {
    isCollapsed.value = length === 0;
  },
  { immediate: true },
);

function createQuestion(text = ''): DynamicScreeningQuestion {
  return { text };
}

function toggleCollapsed() {
  if (props.isDisabled) {
    return;
  }

  isCollapsed.value = !isCollapsed.value;

  // Add one empty question when expanding if no questions exist
  if (editableQuestions.value.length === 0) {
    editableQuestions.value = [createQuestion()];
  }
}

function addQuestion() {
  if (props.isDisabled || editableQuestions.value.length >= MAX_QUESTIONS) {
    return;
  }

  const emptyQuestions = editableQuestions.value.filter((q) => !q?.text).length;
  shouldDisplayErrors.value = emptyQuestions > 0;
  if (emptyQuestions) {
    return;
  }

  // trigger computed setter by assigning
  editableQuestions.value = [...editableQuestions.value, createQuestion()];
}

function removeQuestion(index: number) {
  if (props.isDisabled) {
    return;
  }

  // trigger computed setter by filtering
  editableQuestions.value = editableQuestions.value.filter((_, questionIndex) => {
    return questionIndex !== index;
  });
}

function updateQuestionText(index: number, text: string) {
  const currentQuestions = editableQuestions.value.slice();
  const question = currentQuestions[index];

  if (!question) {
    return;
  }

  currentQuestions[index] = {
    ...question,
    text,
  };

  // trigger computed setter by assigning
  editableQuestions.value = currentQuestions;
}

function getQuestionKey(question: DynamicScreeningQuestion) {
  return question.order ?? editableQuestions.value.indexOf(question);
}

function hasError(question?: DynamicScreeningQuestion): boolean {
  return shouldDisplayErrors.value && !question?.text;
}

function errorMessages(question?: DynamicScreeningQuestion): string | string[] {
  return hasError(question) ? 'Required' : '';
}
</script>

<template>
  <div>
    <button
      class="flex w-full items-center justify-between text-sm font-bold text-highlight-500 disabled:opacity-60"
      type="button"
      @click="toggleCollapsed"
      :disabled="isDisabled"
    >
      <span class="flex items-center">
        <span class="mr-2">+ Add your own questions (optional)</span>
      </span>
      <ChevronDownIcon class="transition-transform" :class="{ 'rotate-180': !isCollapsed }" />
    </button>

    <transition name="fade">
      <div v-if="!isCollapsed">
        <p class="text-sm text-shade-820">
          Your AI recruiter will try to work your questions into the conversation naturally.
        </p>

        <draggable
          v-model="editableQuestions"
          :itemKey="getQuestionKey"
          group="dynamic-guidance"
          handle=".question-draggable-handle"
          class="my-4"
          :disabled="isDisabled"
        >
          <template #item="{ element, index }">
            <v-text-field
              :model-value="element.text"
              density="compact"
              hide-details="auto"
              placeholder="Add question..."
              variant="outlined"
              required
              :disabled="isDisabled"
              :error="hasError(element)"
              :error-messages="errorMessages(element)"
              @update:modelValue="updateQuestionText(index, $event)"
              class="mt-4"
            >
              <template #prepend>
                <div class="question-draggable-handle">
                  <DragDotsSvg />
                </div>
              </template>

              <template #append-inner>
                <button
                  class="clean-btn"
                  type="button"
                  @click="updateQuestionText(index, '')"
                  :disabled="isDisabled"
                >
                  <PlusIcon class="rotate-45" />
                </button>
              </template>

              <template #append>
                <button
                  class="disabled:hidden"
                  type="button"
                  @click="removeQuestion(index)"
                  :disabled="isDisabled"
                >
                  <TrashIcon class="text-shade-800" />
                </button>
              </template>
            </v-text-field>
          </template>
        </draggable>

        <!-- Add question button -->
        <div class="mt-2">
          <button
            class="flex h-10 w-full items-center justify-center rounded-md bg-shade-900 text-base font-semibold text-tint-20 disabled:opacity-50"
            type="button"
            @click="addQuestion"
            :disabled="addButtonDisabled"
          >
            <PlusIcon class="mr-2 text-tint-20" />
            Add question
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;
  background-color: white;

  .v-field__outline {
    @apply text-tint-80;
  }

  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
    cursor: text;
    user-select: text;
  }

  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  .clean-btn {
    display: none;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }

    .clean-btn {
      display: block;
    }
  }
}

.question-draggable-handle {
  @apply flex h-full items-center text-tint-80;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
